import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import TeamLeaderInviteMemberModal from '../../../components/modals/TeamLeaderInviteMemberModal'
import PullToRefresh from '../../../components/PullToRefresh'
import { pluralize } from '../../../utils/pluralize'
import { 
  ArrowLeft,
  MagnifyingGlass,
  UserPlus,
  FadersHorizontal,
  UserCircle,
  ArrowUpRight,
  X,
  CaretDown
} from '@phosphor-icons/react'

export default function TeamLeaderMobileMembers() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState([]) // All members across all departments
  const [filteredMembers, setFilteredMembers] = useState([])
  const [teamData, setTeamData] = useState(null) // Team data (depth 1)
  const [groups, setGroups] = useState([]) // List of groups (depth 2)
  const [subgroups, setSubgroups] = useState([]) // List of departments (depth 3)
  const [selectedGroup, setSelectedGroup] = useState('all') // Group filter
  const [selectedDepartment, setSelectedDepartment] = useState('all') // Department filter
  const [currentUserId, setCurrentUserId] = useState(null)
  const [userData, setUserData] = useState(null)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  
  // Bottom sheet for member details
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [isClosing, setIsClosing] = useState(false)

  // Filter bottom sheet
  const [showFilterSheet, setShowFilterSheet] = useState(false)
  const [tempSelectedGroup, setTempSelectedGroup] = useState('all')
  const [tempSelectedDepartment, setTempSelectedDepartment] = useState('all')
  const [isFilterClosing, setIsFilterClosing] = useState(false)

  useEffect(() => {
    loadMembers()
  }, [selectedGroup, selectedDepartment]) // Reload when group or department filter changes

  useEffect(() => {
    filterMembers()
  }, [members, searchQuery])

  const loadMembers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      setCurrentUserId(user.id)

      const { data: userDataResult } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()
      setUserData(userDataResult)
      
      // Get team they lead (depth 1)
      const { data: teamMembership } = await supabase
        .from('group_members')
        .select(`
          groups!inner (
            id,
            name,
            depth
          )
        `)
        .eq('user_id', user.id)
        .eq('groups.depth', 1)
        .single()

      const team = teamMembership?.groups
      setTeamData(team)

      if (team) {
        // Get all groups (depth 2) under this team
        const { data: groupsData } = await supabase
          .from('groups')
          .select('id, name')
          .eq('parent_id', team.id)
          .eq('depth', 2)
          .order('name')
        
        setGroups(groupsData || [])

        // Get ALL subgroups (departments) under all groups
        const groupIds = groupsData?.map(g => g.id) || []
        let subgroupsData = []
        if (groupIds.length > 0) {
          const { data: allSubgroups } = await supabase
            .from('groups')
            .select('id, name, parent_id')
            .in('parent_id', groupIds)
            .eq('depth', 3)
            .order('name')
          
          subgroupsData = allSubgroups || []
        }
        
        setSubgroups(subgroupsData)

        // Determine which subgroups to query based on filters
        let targetSubgroupIds = []
        
        // First filter by group
        let filteredSubgroups = subgroupsData
        if (selectedGroup !== 'all') {
          filteredSubgroups = subgroupsData.filter(sg => sg.parent_id === selectedGroup)
        }
        
        // Then filter by department
        if (selectedDepartment === 'all') {
          targetSubgroupIds = filteredSubgroups?.map(sg => sg.id) || []
        } else {
          targetSubgroupIds = [selectedDepartment]
        }

        // Get members from filtered department(s)
        let allMemberIds = []
        if (targetSubgroupIds.length > 0) {
          const { data: filteredMembers } = await supabase
            .from('group_members')
            .select('user_id')
            .in('group_id', targetSubgroupIds)
          
          allMemberIds = filteredMembers?.map(m => m.user_id) || []
        }

        if (allMemberIds.length > 0) {
          // Get user details for all members
          const { data: userDetails } = await supabase
            .from('users')
            .select('id, name, email, phone, profile_photo_url, leadership_level')
            .in('id', allMemberIds)

          if (userDetails && userDetails.length > 0) {
            // For each member, get their department and role
            const membersWithRoles = await Promise.all(
              userDetails.map(async (member) => {
                // Get member's department
                const { data: memberDepartment } = await supabase
                  .from('group_members')
                  .select('group_id, groups!inner(id, name, depth)')
                  .eq('user_id', member.id)
                  .eq('groups.depth', 3)
                  .maybeSingle()

                const departmentName = memberDepartment?.groups?.name || 'No Department'

                // Determine role based on leadership_level
                let role = 'Member'
                if (member.leadership_level === 'subgroup') {
                  role = 'H.O.D'
                } else if (member.leadership_level === 'group') {
                  role = 'Head'
                } else if (member.leadership_level === 'team') {
                  role = 'Lead'
                }

                // Calculate attendance rate
                const { data: allEvents } = await supabase
                  .from('events')
                  .select('id')
                  .eq('org_id', userDataResult.org_id)

                const { data: attendanceRecords } = await supabase
                  .from('attendance_records')
                  .select('id')
                  .eq('user_id', member.id)

                const totalEvents = allEvents?.length || 0
                const attended = attendanceRecords?.length || 0
                const attendanceRate = totalEvents > 0 ? Math.round((attended / totalEvents) * 100) : 0

                return {
                  ...member,
                  department: departmentName,
                  role,
                  attendanceRate,
                  isCurrentUser: member.id === user.id
                }
              })
            )

            // Sort members: H.O.D (subgroup leaders) first, then others
            const sortedMembers = membersWithRoles.sort((a, b) => {
              if (a.role === 'H.O.D' && b.role !== 'H.O.D') return -1
              if (a.role !== 'H.O.D' && b.role === 'H.O.D') return 1
              return 0
            })

            setMembers(sortedMembers)
            setFilteredMembers(sortedMembers)
          }
        } else {
          setMembers([])
          setFilteredMembers([])
        }
      }
    } catch (error) {
      console.error('Error loading members:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterMembers = () => {
    if (!searchQuery.trim()) {
      setFilteredMembers(members)
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = members.filter(member =>
      member.name.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query)
    )
    setFilteredMembers(filtered)
  }

  const handleMemberClick = (member) => {
    setSelectedMember(member)
    setShowBottomSheet(true)
    setIsClosing(false)
  }

  const closeBottomSheet = () => {
    setIsClosing(true)
    setTimeout(() => {
      setShowBottomSheet(false)
      setIsClosing(false)
      setSelectedMember(null)
    }, 300)
  }

  // Filter Bottom Sheet Functions
  const openFilterSheet = () => {
    // Sync temp values with current filters
    setTempSelectedGroup(selectedGroup)
    setTempSelectedDepartment(selectedDepartment)
    setShowFilterSheet(true)
  }

  const closeFilterSheet = () => {
    setIsFilterClosing(true)
    setTimeout(() => {
      setShowFilterSheet(false)
      setIsFilterClosing(false)
    }, 300)
  }

  const applyFilters = () => {
    setSelectedGroup(tempSelectedGroup)
    setSelectedDepartment(tempSelectedDepartment)
    closeFilterSheet()
  }

  const getActiveFilterCount = () => {
    let count = 0
    if (selectedGroup !== 'all') count++
    if (selectedDepartment !== 'all') count++
    return count
  }

  const getAvailableDepartments = () => {
    // Return departments based on temp group selection
    if (tempSelectedGroup === 'all') {
      return subgroups
    }
    return subgroups.filter(sg => sg.parent_id === tempSelectedGroup)
  }

  const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return parts[0][0].toUpperCase()
  }

  const getAvatarColor = (name) => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
      '#10B981', '#06B6D4', '#6366F1', '#EF4444'
    ]
    const index = name ? name.charCodeAt(0) % colors.length : 0
    return colors[index]
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'H.O.D':
        return 'bg-purple-50 text-purple-700 border-purple-200'
      case 'Head':
        return 'bg-blue-50 text-blue-700 border-blue-200'
      case 'Lead':
        return 'bg-green-50 text-green-700 border-green-200'
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200'
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading members..." />
  }

  return (
    <>
      <style>{`
        .liquid-glass-button {
          border-radius: 200px;
          height: 40px;
          width: 40px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #2C5DE5;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;  /* Chrome, Safari and Opera */
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        @keyframes slideDown {
          from { transform: translateY(0); }
          to { transform: translateY(100%); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out both;
        }

        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out both;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out both;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out both;
        }
      `}</style>

      <PullToRefresh onRefresh={loadMembers}>
        <div className="bg-white min-h-screen pb-6">
          {/* Header */}
          <div className="bg-white px-6 pt-10 pb-4">
            <div className="flex items-center relative">
              <button
                onClick={() => navigate(-1)}
                className="bg-slate-50 border border-slate-200 rounded-[20px] p-2"
              >
                <ArrowLeft size={20} weight="regular" className="text-slate-600" />
              </button>
              <h1 className="text-xl font-semibold text-slate-800 absolute left-1/2 transform -translate-x-1/2">
                My Team
              </h1>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pt-1">
            {/* Invite Button */}
            <button
              onClick={() => setShowInviteModal(true)}
              className="w-full mb-6 px-4 py-3 bg-[#2C5DE5] text-white rounded-[20px] font-medium hover:bg-[#2248B2] transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus size={20} weight="bold" />
              <span>Invite member</span>
            </button>

            {/* Filter Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Filter Button - Wraps content */}
                <button
                  onClick={openFilterSheet}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-full font-medium hover:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <FadersHorizontal size={18} weight="regular" className="text-slate-600" />
                  <span className="text-slate-700 text-sm">Filter</span>
                </button>

                {/* Active Group Filter Chip */}
                {selectedGroup !== 'all' && (
                  <div className="px-3 py-2 bg-[#EAF0FF] border border-[#ADC2FF] rounded-full flex items-center gap-2">
                    <span className="text-[#2C5DE5] text-sm font-medium">
                      {groups.find(g => g.id === selectedGroup)?.name}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedGroup('all')
                        setSelectedDepartment('all')
                      }}
                      className="hover:bg-[#ADC2FF]/30 rounded-full p-0.5 transition-colors"
                    >
                      <X size={14} weight="bold" className="text-[#2C5DE5]" />
                    </button>
                  </div>
                )}

                {/* Active Department Filter Chip */}
                {selectedDepartment !== 'all' && (
                  <div className="px-3 py-2 bg-[#EAF0FF] border border-[#ADC2FF] rounded-full flex items-center gap-2">
                    <span className="text-[#2C5DE5] text-sm font-medium">
                      {subgroups.find(sg => sg.id === selectedDepartment)?.name}
                    </span>
                    <button
                      onClick={() => setSelectedDepartment('all')}
                      className="hover:bg-[#ADC2FF]/30 rounded-full p-0.5 transition-colors"
                    >
                      <X size={14} weight="bold" className="text-[#2C5DE5]" />
                    </button>
                  </div>
                )}

                {/* Clear All Button - Only shows if any filter active */}
                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={() => {
                      setSelectedGroup('all')
                      setSelectedDepartment('all')
                    }}
                    className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <MagnifyingGlass 
                  size={20} 
                  weight="light" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-[20px] focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 placeholder:text-slate-400"
                />
              </div>
            </div>

            {/* Members Count */}
            <p className="text-sm text-[#475569] font-medium mb-3">
              {pluralize(filteredMembers.length, 'Member')} ({filteredMembers.length})
            </p>

            {/* Members List */}
            <div className="space-y-3">
              {filteredMembers.length === 0 ? (
                <div className="bg-slate-50 rounded-[20px] border border-slate-200 p-8 text-center">
                  <UserCircle size={48} weight="light" className="text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No members found</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {searchQuery ? 'Try a different search term' : 'Invite members to get started'}
                  </p>
                </div>
              ) : (
                filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="bg-white rounded-[20px] border border-slate-200 p-4"
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {member.profile_photo_url ? (
                          <img 
                            src={member.profile_photo_url}
                            alt={member.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="w-16 h-16 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                            style={{ backgroundColor: getAvatarColor(member.name) }}
                          >
                            {getInitials(member.name)}
                          </div>
                        )}
                      </div>

                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-slate-800 truncate">
                          {member.name}
                          {member.isCurrentUser && (
                            <span className="text-slate-500 font-normal"> (You)</span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500 truncate mb-2">
                          {member.email}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block text-xs font-medium px-2 py-1 rounded-lg border ${getRoleBadgeColor(member.role)}`}>
                            {member.role}
                          </span>
                          <span className="inline-block text-xs font-medium px-2 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200">
                            {member.department}
                          </span>
                        </div>
                      </div>

                      {/* Arrow Button */}
                      <button
                        onClick={() => handleMemberClick(member)}
                        className="liquid-glass-button flex items-center justify-center flex-shrink-0"
                      >
                        <ArrowUpRight size={20} weight="bold" className="text-white" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>

      {/* Invite Member Modal */}
      <TeamLeaderInviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        teamId={teamData?.id}
        orgId={userData?.org_id}
        groups={groups}
        subgroups={subgroups}
        onSuccess={loadMembers}
      />

      {/* Member Details Bottom Sheet */}
      {showBottomSheet && selectedMember && (
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bottom-0 bg-black/50 z-[60] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={closeBottomSheet}
          />
          
          {/* Bottom Sheet */}
          <div 
            className={`fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[24px] shadow-xl max-h-[70vh] overflow-hidden ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Member Details</h3>
              <button 
                onClick={closeBottomSheet}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(70vh-80px)] p-5">
              {/* Avatar and Name */}
              <div className="flex flex-col items-center mb-6">
                {selectedMember.profile_photo_url ? (
                  <img 
                    src={selectedMember.profile_photo_url}
                    alt={selectedMember.name}
                    className="w-24 h-24 rounded-full object-cover mb-4"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center text-white font-semibold text-2xl mb-4"
                    style={{ backgroundColor: getAvatarColor(selectedMember.name) }}
                  >
                    {getInitials(selectedMember.name)}
                  </div>
                )}
                <h2 className="text-xl font-bold text-slate-800 text-center">
                  {selectedMember.name}
                </h2>
                <span className={`inline-block text-sm font-medium px-3 py-1 rounded-lg border mt-2 ${getRoleBadgeColor(selectedMember.role)}`}>
                  {selectedMember.role}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Email</label>
                  <p className="text-base text-slate-800 mt-1">{selectedMember.email}</p>
                </div>

                {/* Phone */}
                {selectedMember.phone && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Phone</label>
                    <p className="text-base text-slate-800 mt-1">{selectedMember.phone}</p>
                  </div>
                )}

                {/* Attendance Rate */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Attendance Rate</label>
                  <p className="text-2xl font-bold text-slate-800 mt-1">{selectedMember.attendanceRate}%</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filter Bottom Sheet */}
      {showFilterSheet && (
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bg-black/50 z-[60] ${isFilterClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={closeFilterSheet}
          />
          
          {/* Bottom Sheet */}
          <div 
            className={`fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[24px] shadow-xl overflow-hidden ${isFilterClosing ? 'animate-slideDown' : 'animate-slideUp'}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Filters</h3>
              <button 
                onClick={closeFilterSheet}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Group Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sub Team</label>
                <div className="relative">
                  <select
                    value={tempSelectedGroup}
                    onChange={(e) => {
                      setTempSelectedGroup(e.target.value)
                      setTempSelectedDepartment('all') // Reset department when group changes
                    }}
                    className="appearance-none w-full bg-white border border-slate-300 rounded-lg px-4 py-3 pr-10 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2C5DE5] focus:border-transparent"
                  >
                    <option value="all">All sub teams</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                  <CaretDown size={20} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                </div>
              </div>

              {/* Department Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Department</label>
                <div className="relative">
                  <select
                    value={tempSelectedDepartment}
                    onChange={(e) => setTempSelectedDepartment(e.target.value)}
                    className="appearance-none w-full bg-white border border-slate-300 rounded-lg px-4 py-3 pr-10 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2C5DE5] focus:border-transparent"
                    disabled={getAvailableDepartments().length === 0}
                  >
                    <option value="all">All departments</option>
                    {getAvailableDepartments().map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  <CaretDown size={20} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                </div>
              </div>

              {/* Apply Button */}
              <button
                onClick={applyFilters}
                className="w-full px-4 py-3 bg-[#2C5DE5] text-white rounded-[20px] font-medium hover:bg-[#2248B2] transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}